import React, { useState } from 'react';
import { Box, Typography, Container, Button, Grid, useMediaQuery, useTheme, IconButton, Drawer, Pagination } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import NavBar from '~/components/NavBar';
import Footer from '~/components/Footer';
import SideBar from '~/components/SideBar';
import CauseCard from "~/components/Cause/Card";
import CausesClient from '../api/CausesClient';
import { useApiCall } from '~/single-page/hooks/useApiCall';
import { APP_ROUTES } from '~/single-page/constants/routes';
import SearchField from '~/components/SearchField';
import { useNormalizedSearch } from '~/single-page/hooks/useNormalizedSearch';
import NoItemsMessage from "~/components/NoItemsMessage";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import NGOsMapNew from "~/components/NGOs/MapNew";

const getItemsPerPage = (isMobile, isTablet) => {
  if (isMobile) return 4;     
  if (isTablet) return 6;     
  return 9;                   
};

// Componente do Banner Principal
const HeroBanner = ({ searchTerm, setSearchTerm, setNormalizedSearchTerm, setPage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:480px)');
  const isTablet = useMediaQuery('(max-width:900px)');
  
  // Breakpoints otimizados para melhor responsividade
  const isSmallTablet = useMediaQuery('(min-width:481px) and (max-width:600px)');
  const isMediumTablet = useMediaQuery('(min-width:601px) and (max-width:900px)');
  const isLargeTablet = useMediaQuery('(min-width:901px) and (max-width:1200px)');
  const isSmallDesktop = useMediaQuery('(min-width:1201px) and (max-width:1351px)');
  const isLargeDesktop = useMediaQuery('(min-width:1352px)');
  
  // Função para determinar configurações da imagem baseado nos breakpoints
  const getImageDimensions = () => {
    if (isSmallTablet) {
      return { 
        width: '100%', 
        minHeight: '600px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      };
    }
    if (isMediumTablet) {
      return { 
        width: '100%', 
        minHeight: '650px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      };
    }
    if (isLargeTablet) {
      return { 
        width: '100%', 
        minHeight: '700px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      };
    }
    if (isSmallDesktop) {
      return { 
        width: '100%', 
        minHeight: '750px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      };
    }
    if (isLargeDesktop) {
      return { 
        width: '100%', 
        minHeight: '710px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center -41px'
      };
    }
    return { 
      width: '100%', 
      minHeight: '700px',
      marginBottom: '0px',
      backgroundSize: 'cover',
      backgroundPosition: 'center -80px'
    };
  };

  const imageDimensions = getImageDimensions();
  
  if (isMobile) {
    return (
      <>
        {/* Banner Mobile com conteúdo sobreposto */}
        <Box
          sx={{
            width: '100%',
            minHeight: '400px',
            marginTop: '16px',
            backgroundImage: 'url("/images/main-banner/home_find_your_cause.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            marginBottom: '0px', // Garantir que não há margin negativo
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }
          }}
        >
          {/* Conteúdo sobreposto - Mobile */}
          <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2, px: 3, pt: 10 }}>
            <Box sx={{ textAlign: 'left', color: 'white', mt: -6.5, mb: 2.5 }}>
              {/* Logo */}
              <Box
                component="img"
                src="/images/moving_actions/bora_impactar.png"
                alt="Bora Impactar"
                sx={{
                  maxWidth: '200px',
                  width: '100%',
                  height: 'auto',
                  mb: 3,
                  display: 'block'
                }}
              />
              
              {/* Texto descritivo superior */}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.9rem',
                  mb: 2,
                  color: 'white',
                  lineHeight: 1.4,
                  textAlign: 'left'
                }}
              >
                As causas sociais são o coração da mudança que queremos ver no mundo. São elas que nos movem a agir, que{' '}
                <Box component="span" sx={{ fontWeight: 'bold' }}>nos conectam com propósitos maiores e nos unem em torno de objetivos comuns.</Box>
              </Typography>
              
              {/* Título - Centralizado */}
              <Typography
                variant="h4"
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  mb: 2,
                  color: 'white',
                  textAlign: 'center'
                }}
              >
                Busque uma causa e transforme o mundo
              </Typography>
              
              {/* Campo de pesquisa - Centralizado */}
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="Pesquisar o nome da causa, descrição"
                  value={searchTerm}
                  onChange={(e) => { 
                    setSearchTerm(e.target.value);
                    setNormalizedSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ 
                          color: '#999',
                          marginRight: '-5px',
                          marginLeft: '-7px'
                        }} />
                      </InputAdornment>
                    ),
                    sx: {
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      '& fieldset': { border: 'none' },
                      fontSize: '0.9rem'
                    }
                  }}
                />
              </Box>
              
              {/* Texto descritivo - Centralizado */}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8rem',
                  opacity: 0.9,
                  color: 'white',
                  textAlign: 'center'
                }}
              >
                Procure por nome da causa, descrição, projeto ou ONG
              </Typography>
            </Box>
          </Container>

          {/* Decoração curva azul - Mobile */}
          <Box
            component="img"
            src="/images/sponsors_companies/blue-curve-decorator.png"
            alt="Decoração curva azul"
            sx={{
              position: 'absolute',
              left: '-1px',
              top: '107%',
              transform: 'translateY(-50%)',
              width: '38px',
              height: '103px',
              zIndex: 10,
              opacity: 0.9,
              pointerEvents: 'none'
            }}
          />
        </Box>
      </>
    );
  }
  
  // Layout desktop/tablet
  return (
    <Box
      sx={{
        width: imageDimensions.width,
        minHeight: imageDimensions.minHeight,
        marginTop: imageDimensions.marginTop,
        marginBottom: imageDimensions.marginBottom,
        backgroundImage: 'url("/images/main-banner/home_find_your_cause.png")',
        backgroundSize: imageDimensions.backgroundSize || 'cover',
        backgroundPosition: imageDimensions.backgroundPosition || 'center -99px',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        mx: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1
        }
      }}
    >
      {/* Conteúdo sobreposto - Desktop/Tablet */}
      <Container maxWidth="lg" sx={{ 
        position: 'relative', 
        zIndex: 2, 
        px: 4, 
        py: { xs: 4, sm: 6, md: 8 }, 
        pb: { xs: 4, sm: 6, md: 0 },
        mt: isSmallTablet ? 6 : isMediumTablet ? 8 : isLargeTablet ? 12 : isSmallDesktop ? 14 : { xs: 4, sm: 6, md: 15.625 }
      }}>
        <Box sx={{ color: 'white', mt: -16.75 }}>
          {/* Seção à esquerda com logo e texto descritivo */}
          <Box sx={{ textAlign: 'left', maxWidth: '600px', mb: 4 }}>
            {/* Logo */}
            <Box
              component="img"
              src="/images/moving_actions/bora_impactar.png"
              alt="Bora Impactar"
              sx={{
                maxWidth: { 
                  xs: '200px',
                  sm: isSmallTablet ? '220px' : '300px', 
                  md: '400px' 
                },
                width: 'auto',
                height: 'auto',
                mb: 4,
                display: 'block'
              }}
            />
            
            {/* Texto descritivo superior */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { sm: '1rem', md: '1.4rem' },
                mb: 3,
                color: 'white',
                lineHeight: 1.5,
                textAlign: 'left'
              }}
            >
              As causas sociais são o coração da mudança que queremos ver no mundo. São elas que nos movem a<br /> agir, que{' '}
              <Box component="span" sx={{ fontWeight: 'bold' }}>nos conectam com propósitos maiores e<br /> nos unem em torno de objetivos comuns.</Box>
            </Typography>
          </Box>
          
          {/* Seção centralizada com título, campo e texto */}
          <Box sx={{ textAlign: 'center' }}>
            {/* Título */}
            <Typography
              variant="h2"
              sx={{
                fontSize: { 
                  xs: '1.5rem',
                  sm: isSmallTablet ? '1.7rem' : '2rem', 
                  md: '2.5rem', 
                  lg: '2.4rem' 
                },
                fontWeight: 'bold',
                mb: 4,
                color: 'white',
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Busque uma causa e transforme o mundo
            </Typography>
            
            {/* Campo de pesquisa */}
            <Box sx={{ mb: 3, maxWidth: '863px', mx: 'auto' }}>
            <TextField
              fullWidth
              placeholder="Pesquisar o nome da causa, descrição"
              value={searchTerm}
              onChange={(e) => { 
                setSearchTerm(e.target.value);
                setNormalizedSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999', fontSize: '2.3rem' }} />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '3px 15px',
                  fontSize: { sm: '1rem', md: '1.1rem' },
                  '& fieldset': { border: 'none' },
                  '& input': {
                    padding: '12px 0'
                  }
                }
              }}
            />
          </Box>
          
            {/* Texto descritivo */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { sm: '1rem', md: '1.3rem' },
                opacity: 0.9,
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              Procure por nome da causa, descrição, projeto ou ONG
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Decoração curva branca - Desktop/Tablet */}
      {(isLargeTablet || isSmallDesktop || isLargeDesktop) && (
        <Box
          component="img"
          src="/images/find-your-cause/white_curve_decorator2.png"
          alt="Decoração curva branca"
          sx={{
            display: { xs: 'none', sm: isSmallTablet || isMediumTablet ? 'none' : 'block' },
            position: 'absolute',
            right: 0,
            top: '67%',
            transform: 'translateY(-50%)',
            width: '100px',
            height: '266px',
            zIndex: 10,
            opacity: 0.9,
            pointerEvents: 'none'
          }}
        />
      )}
    </Box>
  );
};

// Componente da Seção de Causas
const CausesSection = ({ searchTerm, filteredCauses, page, setPage }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:600px)');
    const isTablet = useMediaQuery('(max-width:900px)');
  
    const itemsPerPage = getItemsPerPage(isMobile, isTablet);
    const totalPages = Math.ceil(filteredCauses.length / itemsPerPage);
    const paginatedCauses = filteredCauses.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  
    const handlePageChange = (event, value) => {
      setPage(value);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  
    return (
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          width: '100%',
          pt: { xs: 4, sm: 5, md: 6 },
          pb: { xs: 6, sm: 8, md: 10 },
          position: 'relative'
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3, lg: 10 } }}>
          {/* Título da seção */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 6, lg: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 'bold',
                fontSize: { 
                  xs: '1.3rem',  // 460px e menor
                  sm: '1.6rem',  // 600px+
                  md: '1.9rem',  // 900px+
                  lg: '2.2rem'   // 1200px+
                },
                color: '#2E2F32',
                mb: { xs: 1.5, sm: 2, md: 2.5 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: { xs: 0.5, sm: 1 },
                lineHeight: { xs: 1.2, sm: 1.3 }
              }}
            >
              {/* Logo ao lado de "Encontre" */}
              <Box
                component="img"
                src="/images/moving_actions/bora_impactar2.png"
                alt="Logo Bora Impactar"
                sx={{
                  width: { 
                    xs: '24px',  // 460px e menor
                    sm: '30px',  // 600px+
                    md: '40px',  // 900px+
                    lg: '52px'   // 1200px+
                  },
                  height: 'auto',
                  marginTop: { xs: '-12px', sm: '-14px', md: '-16px' },
                  flexShrink: 0
                }}
              />
              <Box component="span" sx={{ whiteSpace: { xs: 'nowrap', sm: 'normal' } }}>
                Encontre
              </Box>
              <Box
                component="span"
                sx={{
                  color: '#009FE3',
                  position: 'relative',
                  display: 'inline-block',
                  minWidth: 'fit-content'
                }}
              >
                sua causa
                <Box
                  component="img"
                  src="/images/moving_actions/blue_underline.png"
                  alt="Underline decorativo"
                  sx={{
                    position: 'absolute',
                    bottom: { xs: '-8px', sm: '-10px' },
                    left: { xs: '-20px', sm: '-25px', md: '-29px' },
                    width: { xs: '130%', sm: '140%', md: '150%' },
                    height: 'auto',
                    maxHeight: { xs: '10px', sm: '11px', md: '13px' },
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#000000',
                opacity: 0.9,
                fontSize: { 
                  xs: '0.85rem',  // 460px e menor
                  sm: '0.95rem',  // 600px+
                  md: '1.05rem',  // 900px+
                  lg: '1.1rem'    // 1200px+
                },
                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                maxWidth: { xs: '320px', sm: '500px', md: '700px', lg: '818px' },
                mx: 'auto',
                textAlign: 'center',
                pl: { xs: 1, sm: 2 },
                pr: { xs: 0, sm: 2 },
                mt: { xs: '20px', sm: 0 }
              }}
            >
              Aqui no Bora Impactar, você encontra diversas causas apoiadas por ONGs e movimentos sociais
              <br />                
              do Recife. Educação, meio ambiente, direitos humanos, cultura... Qual causa vai despertar seu                
              <br />
              desejo de transformar a realidade?
            </Typography>
          </Box>
  
          {/* Decoração curva laranja */}
          <Box
            component="img"
            src="/images/find-your-cause/blue_curve_decorator.png"
            alt="Decoração curva laranja"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              left: -1,
              top: '2%',
              width: '157px',
              height: '255px',
              zIndex: 0,
              opacity: 0.9,
              pointerEvents: 'none'
            }}
          />
  
          {/* Grid de causas - layout responsivo */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {paginatedCauses.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',                    // Mobile: 1 coluna
                      sm: 'repeat(2, 1fr)',        // Tablet pequeno: 2 colunas
                      md: 'repeat(3, 1fr)'         // Desktop: 3 colunas (volta ao original)
                    },
                    gap: { xs: 2, sm: 2, md: 2.5 }, // Espaçamento responsivo entre cards reduzido
                    paddingInline: {
                      '@media (min-width: 900px) and (max-width: 1199px)': { 
                        paddingInline: '132px !important'
                      },
                      '@media (min-width: 1200px) and (max-width: 1253px)': {
                        paddingInline: '80px !important'
                      },
                      '@media (min-width: 1254px) and (max-width: 1354px)': {
                        paddingInline: '50px !important'
                      },
                      '@media (min-width: 1355px) and (max-width: 1590px)': {
                        paddingInline: '71px !important'
                      }
                    },
                    justifyItems: 'center',
                    alignItems: 'stretch',
                    width: '100%',
                    maxWidth: '100%'
                  }}
                >
                  {paginatedCauses.map((cause, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        width: '100%',
                        maxWidth: {
                          xs: '280px',                     // Mobile: reduzido de 300px para 280px
                          '@media (min-width: 900px) and (max-width: 1000px)': {
                            maxWidth: '220px'              // Reduzido de 250px para 220px
                          },
                          '@media (min-width: 1001px) and (max-width: 1253px)': {
                            maxWidth: '270px'              // Reduzido de 300px para 270px
                          },
                          '@media (min-width: 1254px) and (max-width: 1354px)': {
                            maxWidth: '320px'              // Reduzido de 350px para 320px
                          },
                          '@media (min-width: 1355px)': {
                            maxWidth: '360px'              // Reduzido de 400px para 360px
                          }
                        },
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      <CauseCard 
                        cause={cause} 
                        sx={{ 
                          width: '100%',
                          minHeight: { xs: '450px', sm: '470px', md: '490px' },  // Reduzido em ~30-40px
                          maxHeight: { xs: '520px', sm: '510px', md: '530px' },  // Reduzido em ~30-40px
                          display: 'flex',
                          flexDirection: 'column',
                          // Garantir que o card tenha espaço suficiente para os botões
                          '& .MuiCardContent-root': {
                            flex: '1 0 auto',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          },
                          // Estilo específico para a área dos botões
                          '& .action-buttons': {
                            marginTop: 'auto',
                            paddingTop: 2
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
  
                {/* Navegação com setas e traços */}
                {filteredCauses.length > itemsPerPage && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    mt: { xs: 6, sm: 8 },
                    gap: 1,
                    alignItems: 'center'
                  }}>
                    {/* Seta esquerda */}
                    <IconButton
                      onClick={() => handlePageChange(null, page - 1)}
                      disabled={page <= 1}
                      sx={{
                        color: '#3D3D3D',
                        padding: 0,
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        },
                        '&:disabled': {
                          opacity: 0.3
                        }
                      }}
                    >
                      <ArrowBackIosIcon sx={{ fontSize: '20px' }} />
                    </IconButton>

                    {/* Traço esquerdo */}
                    <Box sx={{
                      width: '16px',
                      height: '2px',
                      backgroundColor: '#3D3D3D',
                      mr: 1,
                      ml: -3.3,
                      opacity: page <= 1 ? 0.1 : 1
                    }} />

                    {/* Paginação padrão no centro */}
                    <Pagination 
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      siblingCount={1}
                      boundaryCount={1}
                      hidePrevButton={true}
                      hideNextButton={true}
                      sx={{
                        '& .MuiPagination-ul': {
                          gap: 0.5
                        },
                        '& .MuiPaginationItem-root': {
                          fontSize: '1rem',
                          minWidth: '32px',
                          height: '32px'
                        }
                      }}
                    />

                    {/* Traço direito */}
                    <Box sx={{
                      width: '16px',
                      height: '2px',
                      backgroundColor: '#3D3D3D',
                      ml: 1,
                      mr: -2.6,
                      opacity: page >= totalPages ? 0.1 : 1
                    }} />

                    {/* Seta direita */}
                    <IconButton
                      onClick={() => handlePageChange(null, page + 1)}
                      disabled={page >= totalPages}
                      sx={{
                        color: '#3D3D3D',
                        padding: 0,
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        },
                        '&:disabled': {
                          opacity: 0.3
                        }
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ 
                width: '100%', 
                textAlign: 'center', 
                py: 8 
              }}>
                <Typography color="text.secondary" variant="h6">
                  {searchTerm ? 
                    `Nenhuma causa encontrada com o termo "${searchTerm}"` :
                    'Nenhuma causa disponível no momento'
                  }
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    );
  };

// Componente principal da página
export default function FindYourCausePage() {
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [isClosing, setIsClosing] = React.useState(false);
    const isMobile = useMediaQuery('(max-width:700px)');
    
    // Estados de pesquisa
    const [searchTerm, setSearchTerm] = useState('');
    const [normalizedSearchTerm, setNormalizedSearchTerm] = useState('');
    const [page, setPage] = useState(1);
  
    const {
      data: causes = [],
      pending: loading,
      success,
    } = useApiCall({
      apiCall: CausesClient.index,
      init: true,
    });
  
    const filteredCauses = useNormalizedSearch(causes, normalizedSearchTerm, [
      'name',
      'description',
      'ongs.name'
    ]);
    
    const handleDrawerClose = () => {
      setIsClosing(true);
      setMobileOpen(false);
    };
  
    const handleDrawerTransitionEnd = () => {
      setIsClosing(false);
    };
  
    const handleDrawerToggle = () => {
      if (!isClosing) {
        setMobileOpen(!mobileOpen);
      }
    };
    
    React.useEffect(() => {
      const originalBodyStyle = document.body.style.cssText;
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.backgroundColor = '#f5f5f5';
      
      return () => {
        document.body.style.cssText = originalBodyStyle;
      };
    }, []);
  
    if (loading) {
      return (
        <Box 
          sx={{ 
            width: '100vw', 
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5'
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Carregando causas...
          </Typography>
        </Box>
      );
    }
    
    if (!causes || causes.length === 0) {
      return (
        <Box 
          sx={{ 
            width: '100vw', 
            minHeight: '100vh',
            margin: 0, 
            padding: 0,
            backgroundColor: '#f5f5f5',
            position: 'relative',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <NavBar toggleDrawer={handleDrawerToggle} />
          
          <Box sx={{ mt: { xs: '70px', sm: '80px', md: '88px' } }}>
            <HeroBanner 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setNormalizedSearchTerm={setNormalizedSearchTerm}
              setPage={setPage}
            />
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                width: '100%',
                py: { xs: 6, sm: 8, md: 10 }
              }}
            >
              <Container maxWidth="lg">
                <NoItemsMessage itemName="causa" gender="feminino" />
              </Container>
            </Box>
          </Box>
          <Footer />
        </Box>
      );
    }
  
    return (
      <Box 
        sx={{ 
          width: '100vw', 
          minHeight: '100vh',
          margin: 0, 
          padding: 0,
          backgroundColor: '#f5f5f5',
          position: 'relative',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <NavBar toggleDrawer={handleDrawerToggle} />
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          onTransitionEnd={handleDrawerTransitionEnd}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: isMobile ? "block" : "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: "100%",
              px: 3,
              pt: 2,
              position: 'relative',
              backgroundColor: (theme) => theme.palette.background.default,
            },
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            mb: 2
          }}>
            <IconButton 
              onClick={handleDrawerClose}
              sx={{ color: 'text.primary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <SideBar drawerToggle={handleDrawerClose} />
        </Drawer>
        
        <Box sx={{ mt: { xs: '70px', sm: '80px', md: '88px' } }}>
          <HeroBanner 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setNormalizedSearchTerm={setNormalizedSearchTerm}
            setPage={setPage}
          />
          <CausesSection 
            searchTerm={searchTerm}
            filteredCauses={filteredCauses}
            page={page}
            setPage={setPage}
          />

          {/* Seção do Mapa com decoração laranja */}
          <Box 
            sx={{ 
              position: 'relative', 
              backgroundColor: '#f5f5f5', 
              py: 0
            }}
          >
            {/* Decoração curva laranja - Superior direita */}
            <Box
              component="img"
              src="/images/find-your-cause/orange_curve_decorator3.png"
              alt="Decoração curva laranja"
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                right: -1,
                top: '-32%',
                width: '139px',
                height: '434px',
                opacity: 0.9,
                pointerEvents: 'none'
              }}
            />
            
            {/* Componente do mapa */}
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <NGOsMapNew filteredCauses={filteredCauses} searchTerm={searchTerm} />
            </Box>
          </Box>
        </Box>
        <Footer />
      </Box>
    );
}
